import * as React from 'react';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';

import {
    PortalTextCommon,
    PortalTextImportModals,
    ShareableLink,
} from '../../Models/Api/strongbox.financialportal';
import { ApplicationState } from '../../Store';
import { GetPortalText } from '../../Store/PortalAssets';
import { ShowLink } from '../ShowLink/ShowLink';
import './ShowLinkModal.css';


type InjectedReduxState = {
    commonText: PortalTextCommon | undefined;
    importModalsText: PortalTextImportModals | undefined;
};

type InjectedActionCreators = {};

type LenderLinkModalProps = {
    workspaceName: string;
    onComplete?: () => void;
    linkDetails: Pick<ShareableLink, 'url' | 'expirationTime'> | null;
};

type Props = LenderLinkModalProps & InjectedReduxState & InjectedActionCreators;

// Mirrors the V1 "Link created" modal (LenderLink/LenderLinkModal.tsx shareable branch): the app-standard
// reactstrap Modal (flight-modal + large-title header) with the shared ShowLink body and the Copy link /
// Close footer buttons, so the V2 share-link modal matches V1 exactly (fonts, sizes, colors, button styling).
const LenderLinkModalComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        commonText,
        importModalsText,
        linkDetails,
    } = props;

    const modalTitle = importModalsText?.secureUrlInformation || '';
    const sharedLink = linkDetails?.url || '';

    const onClose = () => props.onComplete && props.onComplete();

    // Custom close control matching the prototype: a 36x36 hit-area with the Font Awesome fa-xmark
    // glyph (native 12x16 at 16px tall), replacing reactstrap's default Bootstrap `btn-close` SVG.
    const closeButton = (
        <button
            type={'button'}
            className={'share-link-modal__close'}
            aria-label={commonText?.closeButton || 'Close'}
            onClick={onClose}
        >
            <FontAwesomeIcon icon={faXmark} />
        </button>
    );

    return (
        <Modal
            // reactstrap renders the modal through a portal on document.body, outside the themed #root, so the
            // design-system tokens (defined under [data-theme]/.theme-data-extraction) are otherwise undefined
            // here and the cr-* styling (font sizes, action colors) falls back to flight-modal's 12px. Carry the
            // theme onto the modal so those tokens resolve for the field/copy/close controls.
            className={'flight-modal share-link-modal theme-data-extraction'}
            isOpen={true}
            backdrop={'static'}
            toggle={onClose}
        >
            <ModalHeader toggle={onClose} close={closeButton} className={'large-title'}>{modalTitle}</ModalHeader>
            <ModalBody>
                <ShowLink
                    copyLinkOnOpen={true}
                    sharedLink={sharedLink}
                    workspaceName={props.workspaceName}
                    linkExpiration={linkDetails?.expirationTime}
                    hideSecureLinkPrompt={true}
                    showCopyButtonInField={true}
                />
            </ModalBody>
            <ModalFooter>
                <div
                    style={{
                        justifyContent: 'flex-end',
                        borderTop: 'none',
                        display: 'flex',
                        width: '100%',
                        margin: 0,
                    }}
                >
                    <button
                        type={'button'}
                        className={'cr-btn cr-btn--primary'}
                        style={{ minWidth: '128px' }}
                        onClick={onClose}
                    >
                        <span className={'cr-btn__label'}>{commonText?.closeButton || ''}</span>
                    </button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export const LenderLinkModal = connect<InjectedReduxState, InjectedActionCreators, LenderLinkModalProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            importModalsText: GetPortalText<PortalTextImportModals>(appState, 'importModals'),
        };
        return result;
    },
    {}
)(LenderLinkModalComponent);
